import { useEffect, useState } from 'react'
import { LoadingBlock, ErrorStrip, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber, fmtPercent } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { FunnelResponse, ProgramResponse } from '@/shared/api/types'
import { getFunnel } from '../api'
import { Pct } from '../parts'

/**
 * The funnel — and the reason it is worth a tab of its own.
 *
 * PollSphere's funnel opened with `ELIGIBLE = every already-enrolled row`. Its
 * enrolment rate was therefore ~100% by construction, and COVERAGE — enrolled
 * over everyone who qualified, the single most important programme metric — did
 * not exist anywhere in the product.
 *
 * Here the first denominator comes from the eligibility snapshot and the
 * numerator from the beneficiary table. Two different places, which is the
 * whole point.
 *
 * ## Every stage except "dropped" is EVER-REACHED
 *
 * `status` is one mutable column, so counting it gives a programme with 100
 * enrolled and 5 still shortlisted an enrolment rate of 20.0. The stages come
 * from the timestamps instead. `dropped` stays a current-status count because it
 * is a state rather than a milestone.
 */
export function FunnelPanel({ program }: { program: ProgramResponse }) {
  const [data, setData] = useState<FunnelResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    getFunnel(program.id)
      .then((d) => { if (alive) setData(d) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [program.id])

  if (error) return <ErrorStrip message={error} />
  if (!data) return <LoadingBlock label="Loading the funnel…" />

  const stages = [
    { label: 'Eligible', value: data.eligible, note: 'from the eligibility snapshot' },
    { label: 'Shortlisted', value: data.shortlisted, note: 'ever reached' },
    { label: 'Enrolled', value: data.enrolled, note: 'ever reached' },
    { label: 'Delivered to', value: data.delivered, note: 'ever reached' },
    { label: 'Completed', value: data.completed, note: 'ever reached' },
  ]
  const max = Math.max(...stages.map((s) => s.value), 1)

  return (
    <div className="space-y-4 px-1">
      {data.eligibilityStale && (
        <Note tone="bad">
          {data.eligibilityStaleReason ??
            'The eligible pool is stale — today’s numerator is being divided by an older denominator.'}
        </Note>
      )}

      <ul className="space-y-2">
        {stages.map((s) => (
          <li key={s.label}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-erp-sm">{s.label}</span>
              <span className="tabular-nums text-erp-sm font-medium">{fmtNumber(s.value)}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-chocolate"
                style={{ width: `${Math.max(1, (s.value / max) * 100)}%` }} />
            </div>
            <span className="text-erp-xs text-muted-foreground">{s.note}</span>
          </li>
        ))}
        <li className="pt-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-erp-sm text-muted-foreground">Dropped</span>
            <span className="tabular-nums text-erp-sm">{fmtNumber(data.dropped)}</span>
          </div>
          <span className="text-erp-xs text-muted-foreground">
            currently dropped — a state, not a stage
          </span>
        </li>
      </ul>

      <dl className="grid grid-cols-2 gap-3">
        <div>
          <dt className="text-erp-xs text-muted-foreground">Coverage</dt>
          <dd className="text-erp-lg font-semibold">
            <Pct value={data.coverage} reason={data.ratesUnavailableReason} />
          </dd>
          <p className="text-erp-xs text-muted-foreground">
            {fmtNumber(data.enrolledLinked)} linked enrolments ÷ {fmtNumber(data.eligible)} eligible
          </p>
        </div>
        <div>
          <dt className="text-erp-xs text-muted-foreground">Enrolment rate</dt>
          <dd className="text-erp-lg font-semibold">
            <Pct value={data.enrolmentRate} reason={data.ratesUnavailableReason} />
          </dd>
        </div>
        <div>
          <dt className="text-erp-xs text-muted-foreground">Delivery rate</dt>
          <dd className="text-erp-lg font-semibold">
            <Pct value={data.deliveryRate} reason={data.ratesUnavailableReason} />
          </dd>
        </div>
        <div>
          <dt className="text-erp-xs text-muted-foreground">Completion</dt>
          <dd className="text-erp-lg font-semibold">
            <Pct value={data.completionRate} reason={data.ratesUnavailableReason} />
          </dd>
        </div>
      </dl>

      {/* Coverage counts LINKED enrolments only, because the eligible pool
          counts voters. A programme with real field enrolment would otherwise
          report coverage above 1. */}
      {data.excludedUnlinkedEnrolled > 0 && (
        <p className="text-erp-xs text-muted-foreground">
          Coverage counts linked enrolments only — the eligible pool is a count of voters.
          {' '}{fmtNumber(data.excludedUnlinkedEnrolled)} enrolled{' '}
          {data.excludedUnlinkedEnrolled === 1 ? 'person is' : 'people are'} not on the register and
          are excluded from that ratio (they are counted in every other stage).
        </p>
      )}

      {data.enrolled > data.shortlisted && (
        <Note tone="info">
          More people are enrolled than were ever shortlisted, which is normal: the field path
          enrols somebody at a chief's camp who was never on a snapshot list.
        </Note>
      )}

      <p className="text-erp-xs text-muted-foreground">
        Computed {fmtDateTime(data.computedAt)}. Rates shown as {fmtPercent(1, 0)} of their stated
        denominator.
      </p>
    </div>
  )
}
